import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AudioLines, Check, FileSpreadsheet, FileText, Mic, Pencil, PhoneOff, Plus, Send, X } from 'lucide-react';
import { RealtimeFunctionCall, useOpenAiRealtime } from './aiAssistant/useOpenAiRealtime';
import { aiAssistantApi, AiAssistantActionProposal, AiAssistantAnswerFacts, AiAssistantCoverageBrief, AiAssistantHolidayAction, AiAssistantStatus } from '../services/aiAssistantApi';
import { AssistantMessageBody } from './aiAssistant/AssistantMessageBody';
import { ProposalCards } from './aiAssistant/ProposalCards';
import { holidayRequestApi } from '../services/api';
import { appConfirm } from '../context/SystemDialogContext';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  askedAt?: string;
  actions?: AiAssistantHolidayAction[];
  actionsTruncated?: boolean;
  proposals?: AiAssistantActionProposal[];
  facts?: AiAssistantAnswerFacts;
  outcomes?: Record<number, 'approved' | 'denied'>;
  actionError?: string;
  denyRequestId?: number;
  denyReason?: string;
  busyRequestId?: number;
}

const TIPS = [
  'Who is working today?',
  'Whose visa is expiring?',
  'Show Maeve’s profile',
  'Is next week published?',
];

function mergeHolidayActions(
  current: AiAssistantHolidayAction[] | undefined,
  incoming: AiAssistantHolidayAction[] | undefined,
): AiAssistantHolidayAction[] {
  const map = new Map<string, AiAssistantHolidayAction>();
  for (const action of [...(current ?? []), ...(incoming ?? [])]) {
    if (action.kind !== 'holiday.approve' && action.kind !== 'holiday.deny') continue;
    map.set(`${action.kind}:${action.requestId}`, action);
  }
  return Array.from(map.values());
}

function readHolidayActions(result: Record<string, unknown>): AiAssistantHolidayAction[] {
  if (!Array.isArray(result.actions)) return [];
  return result.actions.filter((row): row is AiAssistantHolidayAction => {
    if (!row || typeof row !== 'object') return false;
    const action = row as AiAssistantHolidayAction;
    return (action.kind === 'holiday.approve' || action.kind === 'holiday.deny')
      && typeof action.requestId === 'number'
      && typeof action.summary === 'string';
  });
}

function readProposal(result: Record<string, unknown>): AiAssistantActionProposal | null {
  const row = result.proposal;
  if (!row || typeof row !== 'object') return null;
  const proposal = row as AiAssistantActionProposal;
  return typeof proposal.id === 'number' && typeof proposal.summary === 'string' ? proposal : null;
}

function holidayRows(actions: AiAssistantHolidayAction[]) {
  const rows: Array<{
    requestId: number;
    summary: string;
    showCoverage: boolean;
    approve?: AiAssistantHolidayAction;
    deny?: AiAssistantHolidayAction;
  }> = [];
  for (const action of actions) {
    let row = rows.find((item) => item.requestId === action.requestId);
    if (!row) {
      row = {
        requestId: action.requestId,
        summary: action.summary,
        showCoverage: action.showCoverage !== false,
      };
      rows.push(row);
    }
    if (action.kind === 'holiday.approve') row.approve = action;
    if (action.kind === 'holiday.deny') row.deny = action;
  }
  return rows;
}

function HolidayActionButtons({
  message,
  onApprove,
  onStartDeny,
  onReason,
  onCancelDeny,
  onSubmitDeny,
  onCoverage,
}: {
  message: ChatMessage;
  onApprove: (action: AiAssistantHolidayAction) => void;
  onStartDeny: (requestId: number) => void;
  onReason: (reason: string) => void;
  onCancelDeny: () => void;
  onSubmitDeny: (action: AiAssistantHolidayAction) => void;
  onCoverage: (requestId: number, summary: string) => void;
}) {
  const rows = holidayRows(message.actions ?? []);
  if (rows.length === 0) return null;
  return (
    <div className="mt-3 space-y-2 border-t border-neutral-200 pt-2">
      {rows.map((row) => {
        const outcome = message.outcomes?.[row.requestId];
        const busy = message.busyRequestId === row.requestId;
        const denying = message.denyRequestId === row.requestId;
        return (
          <div key={row.requestId} className="space-y-1.5">
            <p className="text-xs text-neutral-600">{row.summary}</p>
            {outcome ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="text-xs font-medium text-neutral-800">{outcome === 'approved' ? 'Approved' : 'Denied'}</p>
                {row.showCoverage && (
                  <button
                    type="button"
                    onClick={() => onCoverage(row.requestId, row.summary)}
                    className="rounded-full border border-neutral-300 bg-white px-2.5 py-1 text-xs font-medium text-neutral-800 hover:bg-neutral-50"
                  >
                    Check Coverage
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {row.showCoverage && (
                  <button
                    type="button"
                    onClick={() => onCoverage(row.requestId, row.summary)}
                    className="rounded-full border border-neutral-300 bg-white px-2.5 py-1 text-xs font-medium text-neutral-800 hover:bg-neutral-50"
                  >
                    Check Coverage
                  </button>
                )}
                {row.approve && (
                  <button
                    type="button"
                    disabled={busy || message.busyRequestId != null}
                    onClick={() => onApprove(row.approve!)}
                    className="rounded-full bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {busy ? 'Saving…' : 'Approve'}
                  </button>
                )}
                {row.deny && !denying && (
                  <button
                    type="button"
                    disabled={busy || message.busyRequestId != null}
                    onClick={() => onStartDeny(row.requestId)}
                    className="rounded-full border border-neutral-300 bg-white px-2.5 py-1 text-xs font-medium text-neutral-800 hover:bg-neutral-50 disabled:opacity-50"
                  >
                    Deny
                  </button>
                )}
              </div>
            )}
            {denying && row.deny && !outcome && (
              <div className="flex flex-col gap-1.5">
                <input
                  value={message.denyReason ?? ''}
                  onChange={(event) => onReason(event.target.value)}
                  placeholder="Reason for denying"
                  className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs text-neutral-800"
                />
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    disabled={busy || !(message.denyReason ?? '').trim()}
                    onClick={() => onSubmitDeny(row.deny!)}
                    className="rounded-full bg-neutral-800 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                  >
                    {busy ? 'Saving…' : 'Deny request'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onCancelDeny}
                    className="rounded-full px-2.5 py-1 text-xs text-neutral-600 hover:bg-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {message.actionsTruncated && (
        <p className="text-[11px] text-neutral-500">Actions are shown for the first 8 pending requests.</p>
      )}
      {message.actionError && (
        <p className="text-[11px] text-red-700">{message.actionError}</p>
      )}
    </div>
  );
}

function PeopleLine({ label, names }: { label: string; names: string[] }) {
  const [open, setOpen] = useState(false);
  const shown = open ? names : names.slice(0, 4);
  const rest = names.length - shown.length;
  return (
    <div className="flex gap-3 text-xs leading-5">
      <span className="w-24 flex-shrink-0 text-neutral-500">{label}</span>
      <span className="min-w-0 text-neutral-800">
        {names.length === 0 ? (
          <span className="text-neutral-400">None</span>
        ) : (
          <>
            {shown.join(', ')}
            {rest > 0 && (
              <button type="button" className="ml-1 font-medium text-blue-700 hover:underline" onClick={() => setOpen(true)}>
                and {rest} more
              </button>
            )}
          </>
        )}
      </span>
    </div>
  );
}

function CoverageFacts({ brief }: { brief: AiAssistantCoverageBrief }) {
  return (
    <div className="space-y-3">
      {(brief.days ?? []).length === 0 && (
        <p className="text-sm text-neutral-500">These dates have already passed.</p>
      )}
      {(brief.days ?? []).map((day) => (
        <section key={day.label} className="overflow-hidden rounded-xl border border-neutral-200">
          <div className="bg-neutral-50 px-3 py-2.5">
            <p className="text-sm font-semibold text-neutral-900">{day.label}</p>
            <p className="text-xs text-neutral-500">{day.shift}</p>
          </div>
          {day.schedules.length === 0 ? (
            <p className="px-3 py-3 text-xs text-neutral-500">No schedule linked.</p>
          ) : (
            day.schedules.map((schedule) => (
              <div key={schedule.name} className="space-y-3 border-t border-neutral-100 px-3 py-3">
                <p className="text-xs font-medium text-neutral-700">{schedule.name}</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-neutral-50 px-2 py-2">
                    <div className="text-lg font-semibold tabular-nums text-neutral-900">{schedule.usual}</div>
                    <div className="text-[11px] text-neutral-500">Usual {day.weekday}</div>
                  </div>
                  <div className="rounded-lg bg-neutral-50 px-2 py-2">
                    <div className="text-lg font-semibold tabular-nums text-neutral-900">{schedule.onRota}</div>
                    <div className="text-[11px] text-neutral-500">On the rota</div>
                  </div>
                  <div className="rounded-lg bg-blue-50 px-2 py-2">
                    <div className="text-lg font-semibold tabular-nums text-blue-800">{schedule.available}</div>
                    <div className="text-[11px] text-blue-700">Available</div>
                  </div>
                </div>
                <div>
                  <p className="mb-1.5 text-[11px] text-neutral-500">Last 4 {day.weekday}s</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {schedule.weeks.map((week) => (
                      <div key={week.label} className="rounded-lg border border-neutral-100 px-1 py-1.5 text-center">
                        <div className="text-[10px] text-neutral-400">{week.label}</div>
                        <div className="text-sm font-semibold tabular-nums text-neutral-800">{week.people}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <PeopleLine label="Leave granted" names={schedule.approved} />
                  <PeopleLine label="Still waiting" names={schedule.pending} />
                  <PeopleLine label="Still free" names={schedule.free} />
                </div>
              </div>
            ))
          )}
        </section>
      ))}
      {brief.truncated && (
        <p className="text-[11px] text-neutral-400">Showing the first 14 days.</p>
      )}
    </div>
  );
}

function HistoryIcon({
  label,
  onClick,
  children,
  danger,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`rounded-md p-1.5 ${danger ? 'text-red-600 hover:bg-red-50' : 'text-neutral-600 hover:bg-white'}`}
    >
      {children}
    </button>
  );
}

type BrowserSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: { results: ArrayLike<{ isFinal: boolean; 0?: { transcript: string } }> }) => void) | null;
  onerror: ((event?: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

function chatAge(value: string) {
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const then = new Date(hasZone ? value : `${value}Z`).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor(Math.max(0, Date.now() - then) / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}yr`;
}

function askedAtLabel(value?: string) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function errorText(e: unknown, fallback: string) {
  const err = e as { response?: { data?: { message?: string } }; message?: string };
  return err.response?.data?.message || (e instanceof Error ? e.message : fallback);
}

const AiAssistant: React.FC = () => {
  const [draft, setDraft] = useState('');
  const [dictatePhase, setDictatePhase] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const [waveLevels, setWaveLevels] = useState<number[]>(() => Array.from({ length: 28 }, () => 0.18));
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<Array<{ id: number; title: string; updatedAt: string }>>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const chatIdRef = useRef<number | null>(null);
  const [status, setStatus] = useState<AiAssistantStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const dictateBaseRef = useRef('');
  const spokenRef = useRef('');
  const dictateEpoch = useRef(0);

  const openaiReady = !!status?.configured;
  const activeTitle = history.find((chat) => chat.id === activeChatId)?.title;

  const rememberChat = useCallback((id: number | null) => {
    chatIdRef.current = id;
    setActiveChatId(id);
  }, []);

  const rememberSavedChat = useCallback((id: number, title: string) => {
    chatIdRef.current = id;
    setActiveChatId(id);
    const updatedAt = new Date().toISOString();
    setHistory((rows) => {
      if (rows.some((row) => row.id === id)) {
        return rows.map((row) => (row.id === id ? { ...row, title: title || row.title, updatedAt } : row));
      }
      return [{ id, title: title || 'New chat', updatedAt }, ...rows];
    });
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const rows = await aiAssistantApi.chats();
      setHistory(rows);
    } catch {
      setHistory([]);
    }
  }, []);

  const appendUser = useCallback((text: string) => {
    setMessages((prev) => {
      if (prev[prev.length - 1]?.role === 'user' && prev[prev.length - 1]?.text === text) return prev;
      return [...prev, { id: `u-${Date.now()}`, role: 'user', text, askedAt: new Date().toISOString() }];
    });
  }, []);

  const queuedActionsRef = useRef<{ actions: AiAssistantHolidayAction[]; truncated: boolean }>({
    actions: [],
    truncated: false,
  });
  const lastAssistantIdRef = useRef<string | null>(null);

  const appendAssistant = useCallback((
    text: string,
    extra?: { actions?: AiAssistantHolidayAction[]; truncated?: boolean; facts?: AiAssistantAnswerFacts; proposals?: AiAssistantActionProposal[] },
  ) => {
    const id = `a-${Date.now()}`;
    lastAssistantIdRef.current = id;
    setMessages((prev) => {
      const queued = queuedActionsRef.current;
      queuedActionsRef.current = { actions: [], truncated: false };
      const actions = mergeHolidayActions(extra?.actions, queued.actions);
      const truncated = Boolean(extra?.truncated || queued.truncated);
      return [...prev, {
        id,
        role: 'assistant' as const,
        text,
        actions: actions.length > 0 ? actions : undefined,
        actionsTruncated: truncated || undefined,
        proposals: extra?.proposals,
        facts: extra?.facts,
      }];
    });
  }, []);

  const offerHolidayActions = useCallback((actions: AiAssistantHolidayAction[], truncated: boolean) => {
    if (actions.length === 0) return;
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.id === lastAssistantIdRef.current && last.role === 'assistant') {
        return prev.map((message) => (message.id === last.id
          ? {
            ...message,
            actions: mergeHolidayActions(message.actions, actions),
            actionsTruncated: message.actionsTruncated || truncated || undefined,
          }
          : message));
      }
      queuedActionsRef.current = {
        actions: mergeHolidayActions(queuedActionsRef.current.actions, actions),
        truncated: queuedActionsRef.current.truncated || truncated,
      };
      return prev;
    });
  }, []);

  const offerProposals = useCallback((proposals: AiAssistantActionProposal[]) => {
    if (proposals.length === 0) return;
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.id === lastAssistantIdRef.current && last.role === 'assistant') {
        const known = new Set((last.proposals ?? []).map((row) => row.id));
        const added = proposals.filter((row) => !known.has(row.id));
        if (added.length === 0) return prev;
        return prev.map((message) => (message.id === last.id
          ? { ...message, proposals: [...(message.proposals ?? []), ...added] }
          : message));
      }
      return [...prev, {
        id: `a-${Date.now()}`,
        role: 'assistant' as const,
        text: 'A change is waiting for confirmation.',
        proposals,
      }];
    });
  }, []);

  const patchMessage = useCallback((id: string, patch: (message: ChatMessage) => ChatMessage) => {
    setMessages((prev) => prev.map((message) => (message.id === id ? patch(message) : message)));
  }, []);
  const holidayBusyRef = useRef(new Set<string>());
  const [inbox, setInbox] = useState<AiAssistantActionProposal[]>([]);
  const [coverage, setCoverage] = useState<{
    title: string;
    brief: AiAssistantCoverageBrief | null;
    loading: boolean;
    error?: string;
  } | null>(null);

  const openCoverage = useCallback(async (requestId: number, summary: string) => {
    setCoverage({ title: summary, brief: null, loading: true });
    try {
      const brief = await aiAssistantApi.holidayCoverage(requestId);
      setCoverage({ title: summary, brief, loading: false });
    } catch (e) {
      setCoverage({
        title: summary,
        brief: null,
        loading: false,
        error: errorText(e, 'Coverage could not be loaded.'),
      });
    }
  }, []);

  const runHolidayAction = useCallback(async (
    message: ChatMessage,
    action: AiAssistantHolidayAction,
    reason?: string,
  ) => {
    const busyKey = `${message.id}:${action.requestId}`;
    if (message.outcomes?.[action.requestId] || holidayBusyRef.current.has(busyKey)) return;
    holidayBusyRef.current.add(busyKey);
    patchMessage(message.id, (row) => ({ ...row, busyRequestId: action.requestId, actionError: undefined }));
    try {
      if (action.kind === 'holiday.approve') {
        const confirmed = await appConfirm(`Approve ${action.summary}?`, { confirmLabel: 'Approve', title: 'Approve holiday' });
        if (!confirmed) {
          patchMessage(message.id, (row) => ({ ...row, busyRequestId: undefined }));
          return;
        }
        await holidayRequestApi.approve(action.requestId);
        patchMessage(message.id, (row) => ({
          ...row,
          busyRequestId: undefined,
          denyRequestId: row.denyRequestId === action.requestId ? undefined : row.denyRequestId,
          outcomes: { ...row.outcomes, [action.requestId]: 'approved' },
        }));
        const chatId = chatIdRef.current;
        if (chatId != null) {
          try {
            const saved = await aiAssistantApi.recordHolidayOutcome(chatId, action.requestId, 'approved');
            rememberSavedChat(saved.chatId, saved.title);
            appendAssistant(saved.text);
          } catch {
            /* The request is already approved. The button shows that. */
          }
        }
        return;
      }

      const trimmed = reason?.trim() ?? '';
      if (!trimmed) {
        patchMessage(message.id, (row) => ({ ...row, busyRequestId: undefined, actionError: 'A reason is required to deny this request.' }));
        return;
      }
      await holidayRequestApi.reject(action.requestId, trimmed);
      patchMessage(message.id, (row) => ({
        ...row,
        busyRequestId: undefined,
        denyRequestId: undefined,
        denyReason: '',
        outcomes: { ...row.outcomes, [action.requestId]: 'denied' },
      }));
      const chatId = chatIdRef.current;
      if (chatId != null) {
        try {
          const saved = await aiAssistantApi.recordHolidayOutcome(chatId, action.requestId, 'denied');
          rememberSavedChat(saved.chatId, saved.title);
          appendAssistant(saved.text);
        } catch {
          /* The request is already denied. The button shows that. */
        }
      }
    } catch (e) {
      patchMessage(message.id, (row) => ({
        ...row,
        busyRequestId: undefined,
        actionError: errorText(e, 'The holiday request could not be updated.'),
      }));
    } finally {
      holidayBusyRef.current.delete(busyKey);
    }
  }, [appendAssistant, patchMessage, rememberSavedChat]);

  const handleRealtimeToolRef = useRef<(call: RealtimeFunctionCall) => void>(() => undefined);

  const realtime = useOpenAiRealtime({
    onUserTranscript: (text) => {
      appendUser(text);
      void aiAssistantApi.appendChat(chatIdRef.current, 'user', text).then((saved) => {
        rememberSavedChat(saved.chatId, saved.title);
        void loadHistory();
      }).catch(() => undefined);
    },
    onAssistantTranscript: (text, done) => {
      if (done) {
        setStreaming('');
        appendAssistant(text);
        void aiAssistantApi.appendChat(chatIdRef.current, 'assistant', text).then((saved) => {
          rememberSavedChat(saved.chatId, saved.title);
        }).catch(() => undefined);
      } else {
        setStreaming(text);
      }
    },
    onFunctionCall: (call) => handleRealtimeToolRef.current(call),
  });

  handleRealtimeToolRef.current = (call) => {
    void (async () => {
      try {
        const result = await aiAssistantApi.executeTool(call.name, call.arguments);
        const actions = readHolidayActions(result);
        const proposal = readProposal(result);
        const forModel = { ...result };
        delete forModel.actions;
        delete forModel.actionsTruncated;
        delete forModel.proposal;
        realtime.sendFunctionOutput(call.callId, forModel);
        if (actions.length > 0) offerHolidayActions(actions, result.actionsTruncated === true);
        if (proposal) offerProposals([proposal]);
      } catch (e) {
        const err = e as { response?: { data?: unknown }; message?: string };
        realtime.sendFunctionOutput(
          call.callId,
          err.response?.data ?? { error: err.message || 'Tool failed' },
        );
      }
    })();
  };

  useEffect(() => {
    let cancelled = false;
    aiAssistantApi
      .status()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) {
          setStatus({ configured: false, chatModel: '', realtimeModel: '' });
          setStatusError('Could not reach Ask Staffly.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy, streaming, realtime.micEnabled]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const refreshUnresolved = useCallback(async () => {
    try {
      const rows = await aiAssistantApi.listUnresolved();
      setInbox((current) => {
        const kept = current.filter((row) => row.status !== 'confirming' && row.status !== 'recovery_required');
        const ids = new Set(kept.map((row) => row.id));
        return [...kept, ...rows.filter((row) => !ids.has(row.id))];
      });
    } catch {
      /* status can be read when the feature switch is off; ignore a dropped connection */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    aiAssistantApi.actionInbox().then((rows) => {
      if (!cancelled) setInbox(rows);
    }).catch(() => undefined);
    void refreshUnresolved();
    return () => { cancelled = true; };
  }, [refreshUnresolved]);

  const saveTitle = async (id: number) => {
    const title = draftTitle.trim();
    if (!title) return;
    const saved = await aiAssistantApi.renameChat(id, title);
    setHistory((rows) => rows.map((row) => (row.id === id ? { ...row, title: saved.title } : row)));
    setEditingId(null);
  };

  const startNewChat = () => {
    rememberChat(null);
    setEditingId(null);
    setDownloadOpen(false);
    setMessages([]);
  };

  const openChat = async (id: number) => {
    const thread = await aiAssistantApi.chatThread(id);
    rememberChat(thread.id);
    setEditingId(null);
    setDownloadOpen(false);
    setMessages(thread.messages.map((m) => ({
      id: `saved-${m.id}`,
      role: m.role === 'user' ? 'user' : 'assistant',
      text: m.content,
      askedAt: m.role === 'user' ? m.createdAt : undefined,
    })));
    void refreshUnresolved();
  };

  const sendText = async (raw: string) => {
    const text = raw.trim();
    if (!text || busy || !openaiReady) return;
    setDraft('');
    queuedActionsRef.current = { actions: [], truncated: false };
    appendUser(text);
    setBusy(true);
    try {
      const transcript = [...messages, { id: '', role: 'user' as const, text }]
        .filter((m) => m.id !== 'welcome')
        .map((m) => ({ role: m.role, content: m.text }));
      const result = await aiAssistantApi.chat(transcript, chatIdRef.current);
      rememberSavedChat(result.chatId, result.title);
      appendAssistant(result.text, {
        actions: result.actions,
        truncated: result.actionsTruncated,
        facts: result.facts,
        proposals: result.proposals,
      });
      void loadHistory();
    } catch (e) {
      appendAssistant(errorText(e, 'OpenAI chat failed.'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (dictatePhase !== 'recording') return undefined;
    const timer = window.setInterval(() => {
      setWaveLevels((prev) => [...prev.slice(1), 0.15 + Math.random() * 0.85]);
    }, 120);
    return () => window.clearInterval(timer);
  }, [dictatePhase]);

  const applySpokenDraft = useCallback(() => {
    const spoken = spokenRef.current.trim();
    const base = dictateBaseRef.current.trim();
    if (spoken) setDraft(base ? `${base} ${spoken}` : spoken);
    spokenRef.current = '';
    setDictatePhase('idle');
  }, []);

  const stopDictation = useCallback(() => {
    dictateEpoch.current += 1;
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    spokenRef.current = '';
    setDictatePhase('idle');
  }, []);

  const toggleDictation = () => {
    if (dictatePhase === 'transcribing') return;
    if (dictatePhase === 'recording') {
      setDictatePhase('transcribing');
      recognitionRef.current?.stop();
      return;
    }
    const speechWindow = window as Window & {
      SpeechRecognition?: new () => BrowserSpeechRecognition;
      webkitSpeechRecognition?: new () => BrowserSpeechRecognition;
    };
    const Ctor = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
    if (!Ctor) {
      setStatusError('This browser cannot dictate. Use the microphone in Chrome, or type the question.');
      return;
    }
    const recognition = new Ctor();
    dictateEpoch.current += 1;
    const epoch = dictateEpoch.current;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    dictateBaseRef.current = draft;
    spokenRef.current = '';
    recognition.onresult = (event) => {
      let finalText = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i += 1) {
        const piece = event.results[i][0]?.transcript ?? '';
        if (event.results[i].isFinal) finalText += piece;
        else interim += piece;
      }
      spokenRef.current = `${finalText}${interim}`.trim();
    };
    recognition.onerror = (event) => {
      if (epoch !== dictateEpoch.current) return;
      if (event?.error === 'aborted') return;
      recognitionRef.current = null;
      spokenRef.current = '';
      setDictatePhase('idle');
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      window.setTimeout(() => {
        if (epoch !== dictateEpoch.current) return;
        applySpokenDraft();
      }, 280);
    };
    recognition.start();
    recognitionRef.current = recognition;
    setWaveLevels(Array.from({ length: 28 }, () => 0.18));
    setDictatePhase('recording');
  };

  const startVoice = async () => {
    if (!openaiReady) return;
    stopDictation();
    if (!realtime.connected) {
      const started = await realtime.connect();
      if (!started) return;
    }
    realtime.setMicEnabled(true);
  };

  useEffect(() => () => recognitionRef.current?.stop(), []);

  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendText(draft);
    }
  };

  const configMessage = statusError
    || (status && !status.configured
      ? 'OpenAI is not configured. Set OpenAI:ApiKey or OPENAI_API_KEY on Staffly.API.'
      : null);

  const savedChat = activeChatId != null;

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-white">
      <aside className="flex w-64 shrink-0 flex-col border-r border-neutral-200 bg-neutral-100">
        <div className="p-3">
          <button
            type="button"
            onClick={startNewChat}
            className="flex w-full items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50"
          >
            <Plus className="h-4 w-4" />
            New
          </button>
        </div>
        <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Chats</p>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {history.length === 0 && (
            <p className="px-2 py-3 text-xs text-neutral-500">No saved chats yet.</p>
          )}
          {history.map((chat) => {
            const selected = chat.id === activeChatId;
            return (
              <div
                key={chat.id}
                className={`group mb-0.5 flex items-center gap-1 rounded-lg px-2 py-2 ${
                  selected ? 'bg-white shadow-sm' : 'hover:bg-neutral-200/80'
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {editingId === chat.id ? (
                    <input
                      value={draftTitle}
                      autoFocus
                      onChange={(event) => setDraftTitle(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') void saveTitle(chat.id);
                        if (event.key === 'Escape') setEditingId(null);
                      }}
                      className="w-full rounded border border-neutral-300 px-2 py-1 text-sm"
                    />
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => void openChat(chat.id)}
                        className="min-w-0 flex-1 truncate text-left text-sm text-neutral-800"
                        title={chat.title}
                      >
                        {chat.title}
                      </button>
                      <span className="shrink-0 text-[11px] text-neutral-400">{chatAge(chat.updatedAt)}</span>
                    </>
                  )}
                </div>
                <div className={`flex shrink-0 items-center ${editingId === chat.id ? '' : 'opacity-0 group-hover:opacity-100'}`}>
                  {editingId === chat.id ? (
                    <>
                      <HistoryIcon label="Save title" onClick={() => void saveTitle(chat.id)}>
                        <Check className="h-4 w-4" />
                      </HistoryIcon>
                      <HistoryIcon label="Cancel" onClick={() => setEditingId(null)}>
                        <X className="h-4 w-4" />
                      </HistoryIcon>
                    </>
                  ) : (
                    <HistoryIcon label="Rename" onClick={() => { setEditingId(chat.id); setDraftTitle(chat.title); }}>
                      <Pencil className="h-4 w-4" />
                    </HistoryIcon>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex-shrink-0 border-b border-neutral-200 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-neutral-900">{activeTitle || 'Ask Staffly'}</h1>
              {!openaiReady && (
                <p className="mt-0.5 text-xs text-neutral-500">
                  {status ? 'OpenAI is not configured' : 'Checking OpenAI…'}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <div className="relative">
                <button
                  type="button"
                  disabled={!savedChat}
                  onClick={() => setDownloadOpen((open) => !open)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-2.5 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Download
                </button>
                {downloadOpen && savedChat && (
                  <div className="absolute right-0 z-10 mt-1 w-40 rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setDownloadOpen(false);
                        if (activeChatId != null) void aiAssistantApi.downloadChat(activeChatId, 'xlsx');
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-neutral-700 hover:bg-neutral-50"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Excel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDownloadOpen(false);
                        if (activeChatId != null) void aiAssistantApi.downloadChat(activeChatId, 'pdf');
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-neutral-700 hover:bg-neutral-50"
                    >
                      <FileText className="h-4 w-4" />
                      PDF
                    </button>
                  </div>
                )}
              </div>
              {openaiReady && realtime.connected && (
                <button
                  type="button"
                  onClick={realtime.disconnect}
                  className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-800"
                >
                  <PhoneOff className="h-3.5 w-3.5" />
                  End voice
                </button>
              )}
            </div>
          </div>

          {configMessage && (
            <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">{configMessage}</p>
          )}
          {realtime.error && (
            <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">{realtime.error}</p>
          )}
          {realtime.connecting && (
            <p className="mt-2 text-xs text-neutral-500">Starting OpenAI Realtime…</p>
          )}
        </header>

        <div ref={threadRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {inbox.length > 0 && (
            <div className="rounded-2xl bg-amber-50 px-3.5 py-2.5 text-sm text-amber-950">
              <p className="mb-1 text-xs font-medium">Waiting for confirmation</p>
              <ProposalCards
                proposals={inbox}
                chatId={chatIdRef.current}
                onSettled={(id, receipt) => setInbox((rows) => rows.map((proposal) => (
                  proposal.id === id ? { ...proposal, status: 'executed', receipt } : proposal
                )))}
              />
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
              {m.role === 'user' && askedAtLabel(m.askedAt) && (
                <p className="mb-1 px-1 text-[11px] text-neutral-400">{askedAtLabel(m.askedAt)}</p>
              )}
              <div
                className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed sm:max-w-[80%] ${
                  m.role === 'user'
                    ? 'rounded-br-md bg-blue-600 text-white'
                    : 'rounded-bl-md bg-neutral-100 text-neutral-800'
                }`}
              >
                {m.role === 'assistant' ? (
                  <AssistantMessageBody text={m.text} />
                ) : (
                  <p className="whitespace-pre-wrap">{m.text}</p>
                )}
                {m.role === 'assistant' && m.facts && (
                  <p className="mt-2 text-[11px] text-neutral-500">
                    {[
                      m.facts.outcome,
                      m.facts.period,
                      m.facts.timezone,
                      ...Object.entries(m.facts.totals ?? {}).map(([key, value]) => `${key} ${value ?? ''}`),
                    ].filter(Boolean).join(' · ')}
                  </p>
                )}
                {m.role === 'assistant' && m.proposals && m.proposals.length > 0 && (
                  <ProposalCards
                    proposals={m.proposals}
                    chatId={chatIdRef.current}
                    onSettled={(id, receipt) => patchMessage(m.id, (row) => ({
                      ...row,
                      proposals: row.proposals?.map((proposal) => (
                        proposal.id === id ? { ...proposal, status: 'executed', receipt } : proposal
                      )),
                    }))}
                  />
                )}
                {m.role === 'assistant' && m.actions && m.actions.length > 0 && (
                  <HolidayActionButtons
                    message={m}
                    onApprove={(action) => { void runHolidayAction(m, action); }}
                    onStartDeny={(requestId) => patchMessage(m.id, (row) => ({
                      ...row,
                      denyRequestId: requestId,
                      denyReason: '',
                      actionError: undefined,
                    }))}
                    onReason={(reason) => patchMessage(m.id, (row) => ({ ...row, denyReason: reason }))}
                    onCancelDeny={() => patchMessage(m.id, (row) => ({
                      ...row,
                      denyRequestId: undefined,
                      denyReason: '',
                    }))}
                    onSubmitDeny={(action) => { void runHolidayAction(m, action, m.denyReason); }}
                    onCoverage={(requestId, summary) => { void openCoverage(requestId, summary); }}
                  />
                )}
              </div>
            </div>
          ))}

          {(realtime.micEnabled || streaming) && (
            <div className="flex justify-start">
              <div className="max-w-[92%] rounded-2xl rounded-bl-md bg-blue-50 px-3.5 py-2.5 text-sm text-blue-900 sm:max-w-[80%]">
                <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  </span>
                  {streaming ? 'Speaking' : 'Listening'}
                </div>
                <p className="whitespace-pre-wrap">{streaming || 'Listening…'}</p>
              </div>
            </div>
          )}

          {busy && (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-md bg-neutral-100 px-3.5 py-2.5 text-sm text-neutral-500">
                Thinking…
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-neutral-200 px-3 py-3">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {TIPS.map((tip) => (
              <button
                key={tip}
                type="button"
                disabled={!openaiReady}
                onClick={() => {
                  setDraft(tip);
                  inputRef.current?.focus();
                }}
                className="rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-[11px] text-neutral-600 hover:bg-white disabled:opacity-50"
              >
                {tip}
              </button>
            ))}
          </div>

          <div className="flex items-end gap-2">
            <div className="flex min-h-12 flex-1 items-end rounded-full border border-neutral-300 bg-neutral-50 pl-4 pr-1 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
              {dictatePhase === 'recording' ? (
                <div className="flex h-12 min-w-0 flex-1 items-center gap-0.5 overflow-hidden">
                  {waveLevels.map((level, index) => (
                    <span
                      key={index}
                      className="w-0.5 flex-shrink-0 rounded-full bg-neutral-900"
                      style={{ height: 4 + level * 20 }}
                    />
                  ))}
                </div>
              ) : dictatePhase === 'transcribing' ? (
                <p className="flex h-12 flex-1 items-center text-sm text-neutral-500">Transcribe</p>
              ) : (
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={draft}
                  disabled={!openaiReady || busy}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onComposerKeyDown}
                  placeholder={openaiReady ? 'Ask Staffly' : 'OpenAI is not configured'}
                  className="max-h-28 min-h-12 flex-1 resize-none bg-transparent py-3 text-sm outline-none disabled:text-neutral-400"
                />
              )}
              <button
                type="button"
                disabled={!openaiReady || busy || realtime.connecting || realtime.connected || dictatePhase === 'transcribing'}
                onClick={toggleDictation}
                className={`mb-1.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full disabled:opacity-40 ${
                  dictatePhase === 'recording' ? 'bg-neutral-900 text-white' : 'text-neutral-800 hover:bg-neutral-200'
                }`}
                aria-label={dictatePhase === 'recording' ? 'End dictation' : 'Dictate'}
                title={dictatePhase === 'recording' ? 'End dictation' : 'Dictate into the box'}
              >
                {dictatePhase === 'transcribing' ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-400 border-t-transparent" />
                ) : dictatePhase === 'recording' ? (
                  <span className="h-3 w-3 rounded-sm bg-white" />
                ) : (
                  <Mic className="h-5 w-5" />
                )}
              </button>
            </div>

            {draft.trim() && !realtime.connected ? (
              <button
                type="button"
                disabled={!openaiReady || busy}
                onClick={() => void sendText(draft)}
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-neutral-300"
                aria-label="Send"
              >
                <Send className="h-5 w-5" />
              </button>
            ) : (
              <button
                type="button"
                disabled={!openaiReady || busy || realtime.connecting}
                onClick={() => {
                  if (realtime.connected) realtime.disconnect();
                  else void startVoice();
                }}
                className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-white disabled:bg-neutral-300 ${
                  realtime.connected ? 'bg-neutral-900 hover:bg-neutral-800' : 'bg-blue-600 hover:bg-blue-700'
                }`}
                aria-label={realtime.connected ? 'End voice' : 'Voice conversation'}
                title={realtime.connected ? 'End voice' : 'Voice conversation'}
              >
                {realtime.connecting ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : realtime.connected ? (
                  <PhoneOff className="h-5 w-5" />
                ) : (
                  <AudioLines className="h-5 w-5" />
                )}
              </button>
            )}
          </div>

          <p className="mt-2 text-[11px] text-neutral-400">
            The mic types what you say into the box. The voice button starts a spoken conversation. Both use live Staffly data.
          </p>
        </div>

        {coverage && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" role="presentation" onClick={() => setCoverage(null)}>
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="coverage-title"
              className="flex max-h-[85vh] w-full max-w-md flex-col rounded-2xl bg-white shadow-xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-4 py-3">
                <div>
                  <h2 id="coverage-title" className="text-sm font-semibold text-neutral-900">Check Coverage</h2>
                  <p className="mt-0.5 text-xs text-neutral-500">
                    {coverage.brief
                      ? `${coverage.brief.employeeName} · ${coverage.brief.leaveType}${coverage.brief.halfDay ? ` · ${coverage.brief.halfDay}` : ''}`
                      : coverage.title}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCoverage(null)}
                  className="rounded-full p-1 text-neutral-500 hover:bg-neutral-100"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="overflow-y-auto px-4 py-3">
                {coverage.loading && <p className="text-sm text-neutral-500">Checking the rota and leave for these dates…</p>}
                {coverage.error && <p className="text-sm text-red-700">{coverage.error}</p>}
                {coverage.brief && <CoverageFacts brief={coverage.brief} />}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiAssistant;
